import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
} from '@mui/material';

import { Location } from 'types';
import { LocationForm } from './types';
import { getCoordinates } from '../../api/analyzerApi';
import { Controller, useForm } from 'react-hook-form';
import GreenButton from 'components/GreenButton';
import WeatherStationsDialog from './WeatherStationsDialog';
import { useCreateLocation } from './api/createLocation';
import { useUpdateLocation } from './api/updateLocation';
import { useWeatherStations } from './api/getWeatherStations';
import { useSystemModules } from 'features/systemModules/api/getSystemModules';

interface Props {
  open: boolean;
  handleClose: () => void;
  location: Location | undefined;
  setSnackBar: (...args: any) => void;
}

const initialFormState: LocationForm = {
  id: null,
  name: '',
  coord_system: 0,
  coord_1: '',
  coord_2: '',
  enabled: false,
  weather_station_id: 0,
};

const LocationDialog = ({
  open,
  handleClose,
  location,
  setSnackBar,
}: Props) => {
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<LocationForm>({
    defaultValues: initialFormState,
    mode: 'onChange',
  });

  const { data: systemModules } = useSystemModules({});
  const [stationDialogOpen, setStationDialogOpen] = useState(false);
  const { data: weatherStations } = useWeatherStations({});
  const createLocation = useCreateLocation({});
  const updateLocation = useUpdateLocation({});

  useEffect(() => {
    if (location)
      reset({
        ...location,
        weather_station_id: location.weather_station_id
          ? location.weather_station_id
          : 0,
      });
    else reset(initialFormState);
  }, [reset, location]);

  const updateStationId = (id: number) => {
    setValue('weather_station_id', id);
  };

  const onSubmit = async () => {
    const locationForm = getValues();
    if (!locationForm.id) {
      try {
        createLocation.mutateAsync({
          ...locationForm,
          enabled: true,
          weather_station_id:
            locationForm.weather_station_id &&
            Number(locationForm.weather_station_id) !== 0
              ? Number(locationForm.weather_station_id)
              : null,
        });
      } catch (err: any) {
        setSnackBar({
          severity: 'error',
          open: true,
          message: 'Location limit exceeded',
        });
      }
    } else {
      const locationFormId = locationForm.id;
      if (locationFormId) {
        updateLocation.mutateAsync({
          data: {
            ...locationForm,
            enabled: !!locationForm.enabled,
            weather_station_id:
              locationForm.weather_station_id &&
              Number(locationForm.weather_station_id) !== 0
                ? Number(locationForm.weather_station_id)
                : null,
          },
          locationId: locationFormId,
        });
      }
    }
    reset(initialFormState);
    handleClose();
  };

  const handleCancel = () => {
    reset(initialFormState);
    handleClose();
  };

  const handleFindCoordinates = async () => {
    const locationForm = getValues();
    const coordinates = await getCoordinates(locationForm.name);
    setValue('coord_system', 0);
    setValue('coord_1', '' + coordinates.latitude);
    setValue('coord_2', '' + coordinates.longitude);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {watch('id') ? 'Edit Location' : 'New Location'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1} direction='column'>
          <Grid item container alignItems='center' spacing={1}>
            <Controller
              name='name'
              control={control}
              rules={{ required: true, minLength: 1 }}
              render={({ field: { onChange, value } }) => (
                <Grid item xs>
                  <TextField
                    id='name'
                    label='Location Name'
                    name='name'
                    value={value}
                    onChange={onChange}
                    margin='dense'
                    fullWidth
                    required
                    error={!!errors.name}
                  />
                </Grid>
              )}
            />
            <Grid item>
              <Button
                variant='contained'
                disabled={!!errors.name || !watch('name')}
                onClick={handleFindCoordinates}
              >
                Find Coordinates
              </Button>
            </Grid>
          </Grid>
          <Controller
            name='coord_system'
            control={control}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <FormControl fullWidth>
                  <InputLabel id='coord-system-select-label'>
                    Coordinate System
                  </InputLabel>
                  <Select
                    labelId='coord-system-select-label'
                    label='Coordinate System'
                    id='coord_system'
                    name='coord_system'
                    onChange={onChange}
                    value={value}
                  >
                    <MenuItem value={0}>Lat/Long</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          />
          <Controller
            name='coord_1'
            control={control}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <TextField
                  id='coord_1'
                  name='coord_1'
                  label='Latitude'
                  fullWidth
                  type='number'
                  onChange={onChange}
                  value={value}
                />
              </Grid>
            )}
          />
          <Controller
            name='coord_2'
            control={control}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <TextField
                  id='coord_2'
                  name='coord_2'
                  label='Longitude'
                  fullWidth
                  type='number'
                  onChange={onChange}
                  value={value}
                />
              </Grid>
            )}
          />
          {systemModules?.WEATHER && (
            <Grid item container spacing={1} alignItems='center'>
              <Controller
                name='weather_station_id'
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Grid item xs>
                    <FormControl fullWidth>
                      <InputLabel id='weather-station-select-label'>
                        Weather Station
                      </InputLabel>
                      <Select
                        labelId='weather-station-select-label'
                        label='Weather Station'
                        id='weather_station_id'
                        name='weather_station_id'
                        onChange={onChange}
                        value={value}
                      >
                        <MenuItem value={0}>
                          <em>None</em>
                        </MenuItem>
                        {weatherStations?.map((station) => (
                          <MenuItem value={station.id} key={station.id}>
                            {station.name + ' (' + station.type + ')'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              />
              <Grid item>
                <GreenButton
                  name='New'
                  onClick={() => {
                    setValue('weather_station_id', 0);
                    setStationDialogOpen(true);
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant='contained'
                  onClick={() => setStationDialogOpen(true)}
                  disabled={!watch('weather_station_id')}
                >
                  Edit
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color='secondary' variant='contained'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={() => handleSubmit(onSubmit)()}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
      <WeatherStationsDialog
        open={stationDialogOpen}
        stationId={Number(watch('weather_station_id'))}
        handleClose={async () => {
          setStationDialogOpen(false);
        }}
        updateStationId={updateStationId}
      />
    </Dialog>
  );
};

export default LocationDialog;
